import { Box, Button, SpinnerButton, TextField, Typography, useRouter } from '@forgeglobal/design-framework'
import { Field, Form, FormRenderProps } from 'react-final-form'
import { Routes } from 'util/Routes'
import { SegmentBuilderField } from 'components/segments/SegmentBuilder'
import {
  SegmentQueriesDocument,
  useCreateOrUpdateSegmentMutation,
  useDownloadQueryMutation,
  useSegmentQuery,
} from 'graphql/bindings/AdminBindings'
import React, { Fragment, useState } from 'react'

const FORCED_CONDITION =
  'computed.accounts.do_not_contact_reasons in [is_in_restricted_location,is_unsubscribed_from_deal_related_emails]'
const BASE_QUERY = `(not ((${FORCED_CONDITION}) or (computed.accounts.do_not_contact_reasons in [is_high_sensitivity])))`

export interface SegmentFormShape {
  id?: string
  name?: string
  query?: string
}

export const SegmentForm: React.FC<FormRenderProps<SegmentFormShape>> = ({ handleSubmit, values }) => {
  const totalCondition = values.query
  const [downloadInProgress, setDownloadInProgress] = useState(false)
  const [downloadCsv] = useDownloadQueryMutation({ variables: { query: totalCondition ?? '' } })
  const [includeCount, setIncludeCount] = useState<number | undefined>()
  const [totalCount, setTotalCount] = useState<number | undefined>()
  const excludeCount = (includeCount ?? 0) - (totalCount ?? 0)

  const handleDownloadClick = () => {
    setDownloadInProgress(true)
    downloadCsv().then(result => {
      setDownloadInProgress(false)
      const url = result.data?.exportAccountsQueryToCsv.url
      if (url) {
        const parsedUrl: { url: string; filename: string } = JSON.parse(url)
        window.open(parsedUrl.url, '_blank')
      }
    })
  }
  return (
    <form onSubmit={handleSubmit}>
      <Box display="flex" alignItems="space-between" justifyContent="space-between">
        <Box width={300} ml={4}>
          <Field name="name" label="Name" component={TextField} />
        </Box>
      </Box>
      <Box display="flex" justifyContent="space-between" ml={4}>
        <Box display="flex" alignItems="flex-end">
          {includeCount && <Box mr={2}>{includeCount} included</Box>}
          {totalCount && <Box mr={2}>{excludeCount} excluded</Box>}
          <Box>{totalCount} total</Box>
        </Box>
        <Box mr={4} display="flex" alignItems="center">
          <SpinnerButton
            variant="contained"
            color="default"
            fullWidth
            loading={downloadInProgress}
            caption="Download"
            onClick={handleDownloadClick}
          />
          <Button color="default" href={Routes.segments()}>
            Cancel
          </Button>
          <Button color="primary" type="submit">
            Save
          </Button>
        </Box>
      </Box>
      <SegmentBuilderField
        name="query"
        forcedCondition={FORCED_CONDITION}
        onTotalCountChange={setTotalCount}
        onPosCountChange={setIncludeCount}
      />
    </form>
  )
}

export const CreateSegmentFormPage: React.FC = () => {
  const [mutate] = useCreateOrUpdateSegmentMutation({ refetchQueries: [{ query: SegmentQueriesDocument }] })
  const { history } = useRouter()
  const handleSubmit = (values: SegmentFormShape) => {
    const form = {
      name: values.name || '',
      query: values.query || '',
    }
    mutate({ variables: { form } }).then(() => history.push(Routes.segments()))
  }
  return (
    <Fragment>
      <Box p={3} pt={4}>
        <Typography variant="h4" color="secondary" style={{ textTransform: 'none' }}>
          Create Segment
        </Typography>
      </Box>
      <Box>
        <Form<SegmentFormShape>
          onSubmit={handleSubmit}
          initialValues={{ query: BASE_QUERY }}
          render={props => <SegmentForm {...props} />}
        />
      </Box>
    </Fragment>
  )
}

export const UpdateSegmentFormPage: React.FC = () => {
  const { history, match } = useRouter<{ id: string }>()
  const segmentId = match.params.id
  const { data, loading } = useSegmentQuery({ variables: { id: segmentId } })
  const [mutate] = useCreateOrUpdateSegmentMutation()

  const handleSubmit = (values: SegmentFormShape) => {
    const form = {
      id: segmentId,
      name: values.name || '',
      query: values.query || '',
    }
    mutate({ variables: { form } }).then(() => history.push(Routes.segments()))
  }

  const initialValues = {
    name: data?.segmentQuery.name,
    query: data?.segmentQuery.query,
  }

  return (
    <Fragment>
      <Box p={3} pt={4}>
        <Typography variant="h4" color="secondary" style={{ textTransform: 'none' }}>
          Update Segment
        </Typography>
      </Box>
      {!loading && (
        <Box>
          <Form<SegmentFormShape>
            onSubmit={handleSubmit}
            initialValues={initialValues}
            render={props => <SegmentForm {...props} />}
          />
        </Box>
      )}
    </Fragment>
  )
}
